import React from "react";
import { Dialog, Transition } from "@headlessui/react";
import propTypes from "prop-types";

const WithFullScreenModal = (Component) => {
  class ComponentWithFullScreenModal extends React.Component {
    constructor(props) {
      super(props);
      this.state = {
        isModalOpen: false,
      };
    }

    openModal = () => {
      if (this.props.disable) alert(this.props.message);
      else {
        this.setState({ isModalOpen: true });
        const functions = this.props.onOpenFunctions;
        functions && functions.forEach((func) => func());

        // Prevent body scrolling when modal is open
        document.body.style.overflow = "hidden";
      }
    };

    closeModal = () => {
      const functions = this.props.onCloseFunctions;
      this.setState({ isModalOpen: false });
      functions && functions.forEach((func) => func());

      // Restore body scrolling when modal is closed
      document.body.style.overflow = "auto";
    };

    render() {
      const { Trigger } = this.props;
      if (!Trigger)
        throw new Error(
          "Trigger element is required. Create a Trigger component and pass it as a prop to the component that is to be converted as a modal."
        );

      return (
        <>
          {/* The UI for the modal trigger is plugged here */}
          <div onClick={this.openModal}>{Trigger && <Trigger />}</div>

          {/* Full Screen Modal Code */}
          <Transition appear show={this.state.isModalOpen} as={React.Fragment}>
            <Dialog
              as="div"
              className="fixed inset-0 z-50 overflow-hidden"
              onClose={this.closeModal}
            >
              {/* Removed min-height and padding for full screen */}
              <div className="h-screen w-screen text-center">
                <Transition.Child
                  as={React.Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <Dialog.Overlay
                    className={`fixed inset-0 bg-black ${
                      this.state.isModalOpen ? "opacity-90" : "opacity-0"
                    }`}
                  />
                </Transition.Child>

                {/* Full screen modal content */}
                <Transition.Child
                  as={React.Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="fixed inset-0 flex items-center justify-center">
                    <div className="w-full h-full flex items-center justify-center bg-white">
                      {/* The component wrapped with WithFullScreenModal HOC */}
                      <Component
                        {...this.props}
                        closeModal={this.closeModal}
                        isFullScreen={true}
                      />
                    </div>
                  </div>
                </Transition.Child>
              </div>
            </Dialog>
          </Transition>
        </>
      );
    }
  }

  ComponentWithFullScreenModal.propTypes = {
    Trigger: propTypes.func.isRequired,
    onOpenFunctions: propTypes.array,
    onCloseFunctions: propTypes.array,
    disable: propTypes.bool,
    message: propTypes.string,
  };

  return ComponentWithFullScreenModal;
};

WithFullScreenModal.propTypes = {
  Component: propTypes.elementType,
};

export default WithFullScreenModal;
